import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import moment from 'moment';
import { useHistory, useParams } from 'react-router-dom';
import { PageTitle, useLayout } from '../../../_metronic/layout/core';
import { DefaultTitle } from '../../../_metronic/layout/components/header/page-title/DefaultTitle';
import saldoInicialLoteService, {
    ISaldoInicialLoteLinha,
    ISaldoInicialLoteLinhaFilter,
    ISaldoInicialLoteResumo,
} from '../../services/saldoInicialLoteService';

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });
const PER_PAGE = 20;

function statusInfo(r: ISaldoInicialLoteResumo): { label: string; color: string; bg: string } {
    if (r.arquivoErro) return { label: 'ERRO', color: '#d9534f', bg: '#d9534f22' };
    if (r.arquivoProcessando) return { label: 'PROCESSANDO', color: '#f0ad4e', bg: '#f0ad4e22' };
    if (r.arquivoProcessou) return { label: 'CONCLUÍDO', color: '#5cb85c', bg: '#5cb85c22' };
    if (r.arquivoIniciouProcesso) return { label: 'INICIADO', color: '#009ef7', bg: '#009ef722' };
    return { label: 'AGUARDANDO', color: '#6c757d', bg: '#6c757d22' };
}

const filtroVazio: ISaldoInicialLoteLinhaFilter = { conta: '', nomeArquivoOrigem: '', importada: undefined };

function StatCard({ label, value, color }: { label: string; value: number | string; color: string }) {
    return (
        <div className='border rounded px-4 py-3' style={{ minWidth: 140 }}>
            <div className='text-muted fs-8 fw-semibold text-uppercase'>{label}</div>
            <div className='fs-2 fw-bold' style={{ color }}>{value}</div>
        </div>
    );
}

function SaldoInicialLoteDetalhe() {
    const { esteiraId, arquivoOperacaoId } = useParams<{ esteiraId: string; arquivoOperacaoId: string }>();
    const history = useHistory();
    const { classes } = useLayout();

    const esteiraIdNum = Number(esteiraId);
    const arquivoIdNum = Number(arquivoOperacaoId);

    const [resumo, setResumo] = useState<ISaldoInicialLoteResumo | null>(null);
    const [loadingResumo, setLoadingResumo] = useState(true);

    const [items, setItems] = useState<ISaldoInicialLoteLinha[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [filtros, setFiltros] = useState<ISaldoInicialLoteLinhaFilter>(filtroVazio);

    useEffect(() => {
        setLoadingResumo(true);
        saldoInicialLoteService.obterResumo(arquivoIdNum)
            .then(setResumo)
            .finally(() => setLoadingResumo(false));
    }, [arquivoIdNum]);

    const carregar = useCallback((pg: number, f: ISaldoInicialLoteLinhaFilter) => {
        setLoading(true);
        saldoInicialLoteService.listarLinhas(esteiraIdNum, { ...f, arquivoOperacaoId: arquivoIdNum, page: pg, perPage: PER_PAGE })
            .then(resp => {
                setItems(resp.items);
                setTotalPages(resp.totalPages);
                setTotal(resp.total);
                setPage(pg);
            })
            .finally(() => setLoading(false));
    }, [esteiraIdNum, arquivoIdNum]);

    useEffect(() => { carregar(1, filtroVazio); }, [carregar]);

    const setF = (patch: Partial<ISaldoInicialLoteLinhaFilter>) => setFiltros(prev => ({ ...prev, ...patch }));
    const onBuscar = () => carregar(1, filtros);
    const onLimpar = () => { setFiltros(filtroVazio); carregar(1, filtroVazio); };
    const onKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter' && !loading) onBuscar(); };

    return (
        <>
            <PageTitle breadcrumbs={[]}>Saldo Inicial em Lote — Detalhe</PageTitle>
            <div className='toolbar' id='kt_toolbar'>
                <div id='kt_toolbar_container' className={clsx(classes.toolbarContainer.join(' '), 'd-flex flex-stack')}>
                    <DefaultTitle />
                </div>
            </div>

            <div className='card mb-4'>
                <div className='card-body'>
                    <button className='btn btn-sm btn-light mb-4' onClick={() => history.push('/andamento-contas')}>
                        ‹ Voltar
                    </button>

                    {loadingResumo || !resumo ? (
                        <div className='d-flex justify-content-center py-6'>
                            <span className='spinner-border spinner-border-sm text-primary me-2 mt-1' />
                            <span>Carregando...</span>
                        </div>
                    ) : (
                        <>
                            <div className='d-flex justify-content-between align-items-start flex-wrap mb-4'>
                                <div>
                                    <h4 className='mb-1'>{resumo.nomeOriginal}</h4>
                                    <div className='text-muted fs-7'>
                                        {resumo.empresaNome} · {moment(resumo.esteiraPeriodoInicio).format('MM/YYYY')} a {moment(resumo.esteiraPeriodoFim).format('MM/YYYY')}
                                    </div>
                                    <div className='text-muted fs-8 mt-1'>
                                        Enviado em {moment(resumo.dataCadastro).format('DD/MM/YYYY HH:mm')}
                                        {resumo.dataFimProcesso && <> · concluído em {moment(resumo.dataFimProcesso).format('DD/MM/YYYY HH:mm')}</>}
                                    </div>
                                </div>
                                {(() => {
                                    const st = statusInfo(resumo);
                                    return (
                                        <span style={{
                                            fontSize: 11, fontWeight: 700, color: st.color, backgroundColor: st.bg,
                                            padding: '5px 14px', borderRadius: 6, whiteSpace: 'nowrap'
                                        }}>
                                            {st.label}
                                        </span>
                                    );
                                })()}
                            </div>

                            <div className='d-flex gap-3 flex-wrap'>
                                <StatCard label='Contas importadas' value={resumo.totalContasImportadas} color='#198754' />
                                <StatCard label='Contas rejeitadas' value={resumo.totalContasRejeitadas} color='#dc3545' />
                                <StatCard label='Linhas lidas' value={resumo.totalLinhas} color='#3F4254' />
                                <StatCard label='Linhas importadas' value={resumo.totalLinhasImportadas} color='#198754' />
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className='card'>
                <div className='card-body'>
                    <div className='d-flex align-items-end gap-2 flex-wrap mb-4'>
                        <div>
                            <label className='form-label fs-7 fw-semibold mb-1'>Conta</label>
                            <input className='form-control form-control-sm' style={{ width: 160 }}
                                value={filtros.conta ?? ''} onChange={e => setF({ conta: e.target.value })} onKeyDown={onKeyDown} />
                        </div>
                        <div>
                            <label className='form-label fs-7 fw-semibold mb-1'>Arquivo de origem</label>
                            <input className='form-control form-control-sm' style={{ width: 220 }}
                                value={filtros.nomeArquivoOrigem ?? ''} onChange={e => setF({ nomeArquivoOrigem: e.target.value })} onKeyDown={onKeyDown} />
                        </div>
                        <div>
                            <label className='form-label fs-7 fw-semibold mb-1'>Importado</label>
                            <select className='form-select form-select-sm' style={{ width: 140 }}
                                value={filtros.importada === undefined ? '' : String(filtros.importada)}
                                onChange={e => setF({ importada: e.target.value === '' ? undefined : e.target.value === 'true' })}>
                                <option value=''>Todos</option>
                                <option value='true'>Sim</option>
                                <option value='false'>Não</option>
                            </select>
                        </div>
                        <button className='btn btn-sm btn-primary' onClick={onBuscar} disabled={loading}>
                            {loading ? <span className='spinner-border spinner-border-sm' role='status' /> : 'Buscar'}
                        </button>
                        <button className='btn btn-sm btn-light' onClick={onLimpar} disabled={loading}>Limpar</button>
                        <span className='text-muted fs-8 ms-auto'>{total} registro{total !== 1 ? 's' : ''}</span>
                    </div>

                    {loading && items.length === 0 ? (
                        <div className='d-flex justify-content-center align-items-center py-10'>
                            <div className='spinner-border text-primary' role='status'>
                                <span className='visually-hidden'>Carregando...</span>
                            </div>
                        </div>
                    ) : (
                        <div className='table-responsive'>
                            <table className='table table-row-dashed table-row-gray-300 align-middle gs-4 gy-3'>
                                <thead>
                                    <tr className='fw-bold text-muted bg-light'>
                                        <th className='min-w-100px ps-4 rounded-start'>Data</th>
                                        <th className='min-w-100px text-end'>Valor</th>
                                        <th className='min-w-120px'>Conta</th>
                                        <th className='min-w-120px'>Conta Participante</th>
                                        <th className='min-w-60px'>Tipo</th>
                                        <th className='min-w-160px'>Arquivo de origem</th>
                                        <th className='min-w-80px text-center'>Importado</th>
                                        <th className='min-w-200px text-end pe-4 rounded-end'>Motivo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} className='text-center text-muted py-8 fs-6'>Nenhuma linha encontrada.</td>
                                        </tr>
                                    ) : (
                                        items.map(item => (
                                            <tr key={item.id}>
                                                <td className='ps-4 fs-7'>{item.data ? moment(item.data).format('DD/MM/YYYY') : '—'}</td>
                                                <td className='text-end fs-7 fw-semibold'>{BRL.format(item.valor)}</td>
                                                <td className='fs-7 text-muted'>{item.contaOriginal || '—'}</td>
                                                <td className='fs-7 text-muted'>{item.contaParticipanteOriginal || '—'}</td>
                                                <td className='fs-7'>{item.tipoLancamento || '—'}</td>
                                                <td className='fs-7' title={item.nomeArquivoOrigem} style={{
                                                    maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                                }}>
                                                    {item.nomeArquivoOrigem}
                                                </td>
                                                <td className='text-center'>
                                                    {item.importada
                                                        ? <span style={{ color: '#198754', fontWeight: 700 }}>✔</span>
                                                        : <span style={{ color: '#dc3545', fontWeight: 700 }}>✘</span>}
                                                </td>
                                                <td className='text-end pe-4 fs-7 text-muted'>{item.motivo || '—'}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {totalPages > 1 && (
                        <div className='d-flex justify-content-between align-items-center mt-3'>
                            <span className='fs-7 text-muted'>Página {page} de {totalPages}</span>
                            <div className='d-flex gap-1'>
                                <button className='btn btn-sm btn-light' disabled={page === 1 || loading} onClick={() => carregar(page - 1, filtros)}>‹</button>
                                <button className='btn btn-sm btn-light' disabled={page === totalPages || loading} onClick={() => carregar(page + 1, filtros)}>›</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default SaldoInicialLoteDetalhe;
